<template>
  <div class="new-project">
    <div class="form-fields">
      <div class="form-field-title">
        {{ $t('projects.newProject.labels.client') }}
      </div>
      <div class="form-field-title">
        {{ $t('projects.newProject.labels.endClient') }}
      </div>
      <div class="form-field-title">
        {{ $t('projects.newProject.labels.projectName') }}
      </div>
    </div>
    <div class="form-fields">
      <div class="form-field">
        <InputField
          v-if="selectedClientName"
          v-model="selectedClientName"
          type="text"
          class="bordered"
          name="project-client"
          picker="minus-circle"
          readonly
          @picker-click="selectedClientName = ''"
        />
        <tol-type-ahead
          v-else
          :search-params="{
            url: '/assignments/user-quick-search',
            searchKeys: ['name'],
            objectType: 'client'
          }"
          class="bordered"
          :menu-label="$t('projects.newProject.labels.searchClients')"
          @select-item="onSelectClient($event)"
        >
          <template v-slot="{ value }">
            {{ value.name }}
          </template>
        </tol-type-ahead>
      </div>
      <div class="form-field">
        <InputField
          v-if="selectedEndClientName"
          v-model="selectedEndClientName"
          type="text"
          class="bordered"
          name="project-end-client"
          picker="minus-circle"
          readonly
          @picker-click="selectedEndClientName = ''"
        />
        <tol-type-ahead
          v-else
          :search-params="{
            url: '/assignments/user-quick-search',
            searchKeys: ['name'],
            objectType: 'client'
          }"
          class="bordered"
          @select-item="onSelectEndClient($event)"
        >
          <template v-slot="{ value }">
            {{ value.name }}
          </template>
        </tol-type-ahead>
      </div>
      <div class="form-field">
        <InputField
          v-model="selectedProjectName"
          type="text"
          class="bordered"
          name="project-name"
        />
      </div>
    </div>
    <div class="form-fields">
      <div class="form-field-title">
        {{ $t('projects.newProject.labels.startDate') }}
      </div>
      <div class="form-field-title">
        {{ $t('projects.newProject.labels.status') }}
      </div>
    </div>
    <div class="form-fields">
      <div class="form-field">
        <FormDatePicker v-model="formattedStartDate" type="bordered" @change="onSelectStartDate" />
      </div>
      <div class="form-field">
        <DropdownSelect
          :menu-items="statuses"
          :selected-item="selectedStatus"
          class="bordered"
          @select-item="selectedStatus = $event"
        />
      </div>
      <div class="form-field create-button-container">
        <TolButton
          :label="$t('projects.newProject.labels.create')"
          button-type="major"
          class="create-button"
          :disabled="!areMandatoryFieldsSelected"
          :is-loading="isLoading"
          @click="createProject"
        />
      </div>
    </div>
  </div>
</template>

<script>
import format from 'date-fns/format';
import { AppActions } from '@/app/app.keys';
import { dateUtils } from '@/mixins/date-utils';
import DropdownSelect from '@/app/components/DropdownSelect';
import FormDatePicker from '@/app/components/FormDatePicker';
import InputField from '@/app/components/InputField';
import { NewProjectActions } from './new-project.keys';
import TolButton from '@/app/components/TolButton';
import TolTypeAhead from '@/app/components/TolTypeAhead';

export default {
  components: {
    DropdownSelect,
    FormDatePicker,
    InputField,
    TolButton,
    TolTypeAhead
  },
  mixins: [dateUtils],
  data() {
    return {
      selectedClientName: '',
      selectedClientReferenceNumber: null,
      selectedEndClientName: '',
      selectedEndClientReferenceNumber: null,
      selectedProjectName: '',
      selectedStartDate: null,
      selectedStatus: null,
      statuses: ['ACTIVE', 'INACTIVE', 'RECONCILED', 'COMPLETED']
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters.IsSavingProject;
    },
    newProject() {
      return this.$store.getters.NewProject;
    },
    formattedStartDate() {
      return this.selectedStartDate ? this.dateFromString(this.selectedStartDate) : null;
    },
    areMandatoryFieldsSelected() {
      return this.selectedClientName && this.selectedEndClientName && this.selectedProjectName;
    }
  },
  async mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('projects.newProject.title'));
  },
  destroyed() {
    this.$store.dispatch(NewProjectActions.SetNewProject);
  },
  methods: {
    onSelectClient(event) {
      this.selectedClientReferenceNumber = event.reference_number;
      this.selectedClientName = event.name;
    },
    onSelectEndClient(event) {
      this.selectedEndClientReferenceNumber = event.reference_number;
      this.selectedEndClientName = event.name;
    },
    onSelectStartDate(event) {
      this.selectedStartDate = format(event, 'YYYY-MM-DD');
    },
    createProject() {
      this.$store.dispatch(NewProjectActions.CreateProject, {
        name: this.selectedProjectName,
        status: this.selectedStatus,
        start_date: this.selectedStartDate,
        client_reference_number: this.selectedClientReferenceNumber,
        end_client_reference_number: this.selectedEndClientReferenceNumber
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
.new-project {
  display: grid;
  grid-template-rows: auto;
  padding: 60px;
  background-color: $white;

  .form-fields {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    margin-top: 50px;

    .form-field-row {
      display: flex;
      align-items: center;
    }

    .form-field-title {
      font-weight: bold;
    }

    .form-field,
    .form-field-title {
      justify-self: center;
      width: 20rem;
      margin-right: 20px;
    }

    .create-button-container {
      display: flex;
      justify-content: flex-end;

      .create-button {
        width: 100%;
      }
    }
  }
}
</style>
